import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { Upload, X } from 'lucide-react'
import { IconSearch } from '@tabler/icons-react'
import Typography from '../../atoms/Typography'
import LayersTable from './LayersTable'
import LayerFilterDialog from 'src/components/organisms/dialogs/LayerFilterDialog'
import { api } from 'src/api/root'
import { SecondaryButton } from 'src/components/atoms/buttons/SecondaryButton'
import { ErrorMessage } from 'src/components/atoms/ErrorMessage'
import { Input } from 'src/components/atoms/inputs/Input'
import { ApiError } from 'src/enviroment/api/errorHandler'
import { LayerUploadStatus } from 'src/types/layer'
import { fileExtension } from 'src/enviroment/utils/fileExtension'
import { useLayerFilters } from 'src/enviroment/hooks/useLayerFilters'

const FileChunksTable = () => {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // API Hooks
  const layerFilters = useLayerFilters()
  const {
    data: layersData,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = api.layer.list.useInfiniteQuery({ query: layerFilters.debouncedSearch, ...layerFilters.filters })
  const { data: uploadedLayer } = api.layer.uploadStatus.useQuery(uploadingFileName)
  const { upload } = api.layer.directUpload.useUpload()
  const layers = useMemo(() => layersData?.pages.flatMap((page) => page.layers) ?? [], [layersData])

  const resetUpload = useCallback(() => {
    setIsUploading(false)
    setUploadingFileName(null)
    setUploadProgress(0)
  }, [])

  // Clear the progress bar once the layer exists. The callback creates it
  // as NEEDS_REVIEW (platform uploads await admin review), so that also counts as uploaded.
  useEffect(() => {
    if (
      uploadedLayer?.status === LayerUploadStatus.COMPLETED ||
      uploadedLayer?.status === LayerUploadStatus.NEEDS_REVIEW
    ) {
      resetUpload()
    }
  }, [uploadedLayer, resetUpload])

  // Allowed MIME types based on the error message
  const ALLOWED_MIME_TYPES = [
    'image/tiff',
    'application/geopackage+sqlite3',
    'application/x-sqlite3',
    'application/gpkg', // Common GPKG MIME type
    'application/geopackage', // Alternative GPKG MIME type
    'application/parquet', // Parquet MIME type
    'application/x-parquet', // Alternative Parquet MIME type
    'application/vnd.apache.parquet', // Apache Parquet MIME type
    'application/octet-stream', // Fallback for GPKG files not properly recognized
  ]

  // Define allowed extensions
  const ALLOWED_EXTENSIONS = ['tiff', 'tif', 'gpkg', 'parquet']

  const handleFileSelection = async (file: File) => {
    setFileError(null)

    const extension = fileExtension(file.name) ?? ''

    // Validate file type using MIME type OR file extension
    const isValidMimeType = file.type && ALLOWED_MIME_TYPES.includes(file.type)
    const isValidExtension = ALLOWED_EXTENSIONS.includes(extension)

    if (!isValidMimeType && !isValidExtension) {
      console.log('File type:', file.type)
      console.log('File extension:', extension)
      setFileError(
        `Invalid file type. Only TIFF images (.tiff, .tif), GeoPackage files (.gpkg), and Parquet files (.parquet) are allowed.`,
      )
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    try {
      setIsUploading(true)
      setUploadProgress(0)
      setUploadingFileName(file.name)
      abortControllerRef.current = new AbortController()
      await upload(file, abortControllerRef.current.signal, ({ percentage }) => setUploadProgress(percentage))
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Upload failed:', error)
        const isDuplicate = (error as ApiError).status === 409
        setFileError(
          isDuplicate
            ? `A file with the name "${file.name}" already exists. Please rename the file or delete the existing one.`
            : 'Upload failed. Please try again.',
        )
      }
      resetUpload()
    } finally {
      abortControllerRef.current = null
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // After the blob is sent, Upload stays locked: with no row yet, a same-name file would overwrite it unchecked.
  const cancelUpload = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort()
    else setIsUploading(false)
  }

  const processingLabel = uploadProgress < 100 ? 'Uploading...' : 'Processing...'
  const uploadButtonLabel = uploadingFileName ? processingLabel : 'Upload File'

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between gap-8">
        <Typography variant="xl" className="shrink-0 text-light-green-50">
          Library
        </Typography>
        <div className="ml-auto flex items-center gap-6">
          <Input
            containerClassName="w-[300px]"
            icon={<IconSearch />}
            placeholder="Search by file name"
            maxLength={100}
            onChange={(e) => layerFilters.setSearch(e.target.value)}
            value={layerFilters.search}
          />
          <LayerFilterDialog filters={layerFilters} />
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".tiff,.tif,.gpkg,.parquet"
            onChange={(e) => e.target.files?.[0] && handleFileSelection(e.target.files[0])}
          />
          <SecondaryButton
            className="flex-shrink-0"
            leftIcon={<Upload className="h-5 w-5 " />}
            onClick={() => fileInputRef.current?.click()}
            disabled={Boolean(uploadingFileName)}
          >
            {uploadButtonLabel}
          </SecondaryButton>
        </div>
      </div>

      <div className="flex items-center justify-between gap-8">
        <Typography variant="sm" className="text-gray-400">
          Accepted file types: TIFF images (.tiff, .tif), GeoPackage files (.gpkg), and Parquet files (.parquet)
        </Typography>
        <Typography variant="sm" className="shrink-0 text-gray-400">
          {layersData?.pages[0]?.total ?? 0} layers
        </Typography>
      </div>

      {fileError && <ErrorMessage message={fileError} />}

      {isUploading && (
        <div className="rounded-lg bg-gray-800 p-4">
          <div className="mb-3 flex items-center justify-between text-white">
            <Typography variant="base">
              {uploadProgress < 100 ? `Uploading file... ${uploadProgress}%` : 'Processing file...'}
            </Typography>
            <button onClick={cancelUpload} className="text-gray-400 transition-colors hover:text-red-500">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="relative h-2 overflow-hidden rounded-full bg-gray-700">
            {uploadProgress < 100 ? (
              <div
                className="h-full rounded-full bg-light-green-600 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            ) : (
              <div className="absolute h-full w-1/3 animate-infinite-progress rounded-full bg-light-green-600" />
            )}
          </div>
        </div>
      )}

      {isError && <ErrorMessage message="Could not load the layers." />}

      <LayersTable
        layers={layers}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={fetchNextPage}
      />
    </div>
  )
}

export default FileChunksTable
